import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Music, FileText, CreditCard,
  Calendar, ChevronRight, AlertCircle, TrendingUp,
  Euro, ArrowRight, ClipboardList, ShoppingBag
} from 'lucide-react';
import {
  inscriptions, produits, factures, paiements, seances,
  getCoursProduits, formatMontant
} from '../data/mockData';

function Dashboard() {
  const totalInscriptions = inscriptions.length;
  const inscriptionsEnAttente = inscriptions.filter(i => i.statut === 'en_attente').length;
  const totalCours = getCoursProduits().length;
  const revenus = paiements.reduce((sum, p) => sum + p.montant, 0);

  const facturesImpayees = factures.filter(f => f.statut === 'impayee');
  const facturesPartielles = factures.filter(f => f.statut === 'partielle');
  const dernieresInscriptions = [...inscriptions].sort((a, b) => b.dateInscription.localeCompare(a.dateInscription)).slice(0, 5);
  const prochainesSeances = seances.filter(s => s.statut === 'planifiee').sort((a, b) => a.date.localeCompare(b.date)).slice(0, 5);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Tableau de bord</h1>
        <p>Bienvenue sur Harmonie — vue d'ensemble de votre école de musique</p>
      </div>

      <div className="dashboard">
        <div className="quick-actions">
          <Link to="/inscriptions" className="quick-action-card">
            <div className="quick-action-icon" style={{ backgroundColor: '#7c3aed' }}>
              <ClipboardList size={24} color="#fff" />
            </div>
            <span>Inscriptions</span>
            <ArrowRight size={16} className="quick-action-arrow" />
          </Link>
          <Link to="/seances" className="quick-action-card">
            <div className="quick-action-icon" style={{ backgroundColor: '#16a34a' }}>
              <Calendar size={24} color="#fff" />
            </div>
            <span>Séances</span>
            <ArrowRight size={16} className="quick-action-arrow" />
          </Link>
          <Link to="/facturation" className="quick-action-card">
            <div className="quick-action-icon" style={{ backgroundColor: '#2563eb' }}>
              <FileText size={24} color="#fff" />
            </div>
            <span>Facturation</span>
            <ArrowRight size={16} className="quick-action-arrow" />
          </Link>
          <Link to="/produits" className="quick-action-card">
            <div className="quick-action-icon" style={{ backgroundColor: '#ea580c' }}>
              <ShoppingBag size={24} color="#fff" />
            </div>
            <span>Produits</span>
            <ArrowRight size={16} className="quick-action-arrow" />
          </Link>
        </div>

        <div className="dashboard-grid">
          <div className="dashboard-card stat-card-large">
            <div className="stat-card-header">
              <div className="stat-icon-large" style={{ backgroundColor: '#ede9fe' }}>
                <Users size={28} color="#7c3aed" />
              </div>
            </div>
            <div className="stat-card-content">
              <span className="stat-label">Inscriptions</span>
              <span className="stat-value-large">{totalInscriptions}</span>
            </div>
            <div className="stat-card-footer">
              <span>{inscriptionsEnAttente} en attente</span>
              <Link to="/inscriptions">Voir <ChevronRight size={14} /></Link>
            </div>
          </div>

          <div className="dashboard-card stat-card-large">
            <div className="stat-card-header">
              <div className="stat-icon-large" style={{ backgroundColor: '#fef3c7' }}>
                <Music size={28} color="#d97706" />
              </div>
            </div>
            <div className="stat-card-content">
              <span className="stat-label">Cours actifs</span>
              <span className="stat-value-large">{totalCours}</span>
            </div>
            <div className="stat-card-footer">
              <span>{produits.length} produits</span>
              <Link to="/produits">Voir <ChevronRight size={14} /></Link>
            </div>
          </div>

          <div className="dashboard-card stat-card-large">
            <div className="stat-card-header">
              <div className="stat-icon-large" style={{ backgroundColor: '#dcfce7' }}>
                <Calendar size={28} color="#16a34a" />
              </div>
            </div>
            <div className="stat-card-content">
              <span className="stat-label">Séances</span>
              <span className="stat-value-large">{seances.length}</span>
            </div>
            <div className="stat-card-footer">
              <span>{prochainesSeances.length} à venir</span>
              <Link to="/seances">Voir <ChevronRight size={14} /></Link>
            </div>
          </div>

          <div className="dashboard-card stat-card-large">
            <div className="stat-card-header">
              <div className="stat-icon-large" style={{ backgroundColor: '#dbeafe' }}>
                <Euro size={28} color="#2563eb" />
              </div>
            </div>
            <div className="stat-card-content">
              <span className="stat-label">Revenus encaissés</span>
              <span className="stat-value-large">{formatMontant(revenus)}</span>
            </div>
            <div className="stat-card-footer">
              <span>{factures.length} factures</span>
              <Link to="/facturation">Voir <ChevronRight size={14} /></Link>
            </div>
          </div>
        </div>

        <div className="dashboard-row">
          <div className="dashboard-card alerts-card">
            <div className="card-header">
              <h3><AlertCircle size={20} /> Alertes</h3>
            </div>
            <div className="alerts-list">
              {inscriptionsEnAttente > 0 && (
                <div className="alert-item alert-warning">
                  <ClipboardList size={18} />
                  <div className="alert-content">
                    <p><strong>{inscriptionsEnAttente} inscription(s) en attente de validation</strong></p>
                    <span className="alert-detail">
                      À traiter rapidement
                    </span>
                  </div>
                  <Link to="/inscriptions"><ChevronRight size={16} /></Link>
                </div>
              )}
              {facturesImpayees.length > 0 && (
                <div className="alert-item alert-danger">
                  <AlertCircle size={18} />
                  <div className="alert-content">
                    <p><strong>{facturesImpayees.length} facture(s) impayée(s)</strong></p>
                    <span className="alert-detail">
                      Montant : {formatMontant(facturesImpayees.reduce((sum, f) => sum + f.totalNet, 0))}
                    </span>
                  </div>
                  <Link to="/facturation"><ChevronRight size={16} /></Link>
                </div>
              )}
              {facturesPartielles.length > 0 && (
                <div className="alert-item alert-warning">
                  <CreditCard size={18} />
                  <div className="alert-content">
                    <p><strong>{facturesPartielles.length} facture(s) partiellement payée(s)</strong></p>
                  </div>
                  <Link to="/paiements"><ChevronRight size={16} /></Link>
                </div>
              )}
              {inscriptionsEnAttente === 0 && facturesImpayees.length === 0 && facturesPartielles.length === 0 && (
                <div className="empty-alerts">
                  <p>Aucune alerte pour le moment.</p>
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-header">
              <h3><TrendingUp size={20} /> Dernières inscriptions</h3>
            </div>
            <div className="recent-list">
              {dernieresInscriptions.map(ins => (
                <div key={ins.id} className="recent-item" style={{ cursor: 'default' }}>
                  <div className="recent-avatar" style={{ background: ins.color || '#8B5CF6' }}>
                    {ins.initials}
                  </div>
                  <div className="recent-info">
                    <strong>{ins.eleve.prenom} {ins.eleve.nom}</strong>
                    <span>{ins.dateInscription}</span>
                  </div>
                  {ins.statut === 'en_attente' && <span className="badge badge-warning">En attente</span>}
                  {ins.statut === 'validee' && <span className="badge badge-primary">Validée</span>}
                  {ins.statut === 'facturee' && <span className="badge badge-success">Facturée</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
